export const LOCAL_SEARCH_DB_PREFIX = "nurion-chat-search-";

// v3 adds the optional `msgtype` column. It is additive: pre-v3 rows
// carry no recoverable msgtype, so they remain messages — there is no
// row to rewrite, only the version bump that admits the new field.
const DB_VERSION = 3;
const EVENTS_STORE = "events";
const POSTINGS_STORE = "postings";
const MAX_BODY_CHARS = 4096;
export const MAX_INDEX_BYTES = 100 * 1024 * 1024;
export const INDEX_EVICTION_PROBE_INTERVAL = 100;
const EVENT_ROW_OVERHEAD_BYTES = 64;
const POSTING_ROW_OVERHEAD_BYTES = 32;
const textEncoder = new TextEncoder();
const STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "for",
  "in",
  "is",
  "of",
  "on",
  "or",
  "the",
  "to",
]);

export interface LocalSearchEventInput {
  eventId: string;
  roomId: string;
  sender: string;
  ts: number;
  body: string;
  /** Matrix message `msgtype` (e.g. `m.text`, `m.file`). Drives the
   *  search palette's Files dimension. Absent on rows written before
   *  the v3 schema bump — those classify as messages. */
  msgtype?: string;
}

export interface LocalSearchEventRow extends LocalSearchEventInput {
  body: string;
}

export interface LocalSearchIndexChanges {
  upsertEvents?: LocalSearchEventInput[];
  deleteEventIds?: string[];
}

export interface LocalSearchIndexConfig {
  maxIndexBytes?: number;
  evictionProbeInterval?: number;
}

export interface LocalSearchQueryPage {
  events: LocalSearchEventRow[];
  nextCursor: string | null;
}

interface PostingRow {
  id: string;
  term: string;
  eventId: string;
}

interface StoredLocalSearchEventRow extends LocalSearchEventRow {
  sizeBytes: number;
}

interface NormalisedLocalSearchEventRow extends StoredLocalSearchEventRow {
  terms: string[];
}

const writeQueues = new Map<string, Promise<void>>();
const insertCounts = new Map<string, number>();

export function localSearchDbName(userId: string): string {
  return `${LOCAL_SEARCH_DB_PREFIX}${userId}`;
}

export function tokeniseSearchText(text: string): string[] {
  const terms = new Set<string>();
  for (const match of text.toLocaleLowerCase().matchAll(/[\p{L}\p{N}]+/gu)) {
    const term = match[0];
    if (term.length < 2 || STOP_WORDS.has(term)) continue;
    terms.add(term);
  }
  return [...terms];
}

export async function upsertLocalSearchEvents(
  userId: string,
  events: LocalSearchEventInput[],
  config?: LocalSearchIndexConfig,
): Promise<void> {
  await applyLocalSearchIndexChanges(userId, { upsertEvents: events }, config);
}

export async function deleteLocalSearchEvent(userId: string, eventId: string): Promise<void> {
  await applyLocalSearchIndexChanges(userId, { deleteEventIds: [eventId] });
}

export async function applyLocalSearchIndexChanges(
  userId: string,
  changes: LocalSearchIndexChanges,
  config: LocalSearchIndexConfig = {},
): Promise<void> {
  return enqueueUserWrite(userId, () => applyLocalSearchIndexChangesNow(userId, changes, config));
}

async function applyLocalSearchIndexChangesNow(
  userId: string,
  changes: LocalSearchIndexChanges,
  config: LocalSearchIndexConfig,
): Promise<void> {
  const deleteEventIds = [...new Set(changes.deleteEventIds ?? [])].filter(Boolean);
  const deleted = new Set(deleteEventIds);
  const rows = (changes.upsertEvents ?? [])
    .filter((event) => !deleted.has(event.eventId))
    .map(normaliseEventRow)
    .filter((row): row is NormalisedLocalSearchEventRow => row !== null);
  if (!userId || (deleteEventIds.length === 0 && rows.length === 0)) return;
  await withSearchStores(userId, "readwrite", (stores) => {
    deleteEventsSequentially(stores, deleteEventIds, 0, () => {
      writeRowsSequentially(stores, rows, 0);
    });
  });
  scheduleEvictionProbe(userId, rows.length, config);
}

export async function wipeLocalSearchForUser(userId: string): Promise<void> {
  if (!userId || typeof indexedDB === "undefined") return;
  await writeQueues.get(userId)?.catch(() => undefined);
  insertCounts.delete(userId);
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(localSearchDbName(userId));
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
}

function enqueueUserWrite(userId: string, run: () => Promise<void>): Promise<void> {
  if (!userId) return Promise.resolve();
  const previous = writeQueues.get(userId)?.catch(() => undefined) ?? Promise.resolve();
  const next = previous.then(run);
  const tracked = next.finally(() => {
    if (writeQueues.get(userId) === tracked) writeQueues.delete(userId);
  });
  writeQueues.set(userId, tracked);
  return next;
}

export async function evictLocalSearchIndex(
  userId: string,
  targetBytes = MAX_INDEX_BYTES,
): Promise<void> {
  if (!userId || targetBytes < 0) return;
  await enqueueUserWrite(userId, () => evictLocalSearchIndexNow(userId, targetBytes));
}

export async function searchLocalEvents(
  userId: string,
  query: string,
  roomId?: string,
): Promise<LocalSearchEventRow[]> {
  const terms = tokeniseSearchText(query);
  if (!userId || terms.length === 0) return [];
  const page = await queryIndex(userId, terms, null, Number.POSITIVE_INFINITY, roomId);
  return page.events;
}

export async function queryIndex(
  userId: string,
  tokens: string[],
  cursor: string | null = null,
  limit = 20,
  roomId?: string,
): Promise<LocalSearchQueryPage> {
  const terms = [...new Set(tokens.map((token) => tokeniseSearchText(token)).flat())];
  const pageSize = Number.isFinite(limit) ? Math.max(0, limit) : Number.POSITIVE_INFINITY;
  if (!userId || terms.length === 0 || pageSize === 0) return { events: [], nextCursor: null };
  const rows: LocalSearchEventRow[] = [];
  let cursorMissing = false;
  try {
    await withSearchStores(userId, "readonly", (stores) => {
      if (!cursor) {
        loadPagedEvents(stores, terms, rows, pageSize, undefined, roomId);
        return;
      }
      const cursorReq = stores.events.get(cursor);
      cursorReq.onsuccess = () => {
        const cursorRow = cursorReq.result as StoredLocalSearchEventRow | undefined;
        if (!cursorRow || (roomId && cursorRow.roomId !== roomId)) {
          cursorMissing = true;
          return;
        }
        eventHasTerms(stores.postings, cursorRow.eventId, terms, (matches) => {
          if (!matches) {
            cursorMissing = true;
            return;
          }
          loadPagedEvents(stores, terms, rows, pageSize, cursorRow, roomId);
        });
      };
    });
  } catch {
    return { events: [], nextCursor: null };
  }
  if (cursorMissing) return { events: [], nextCursor: null };
  const events = rows.slice(0, pageSize);
  const nextCursor = rows.length > events.length ? (events.at(-1)?.eventId ?? null) : null;
  return { events, nextCursor };
}

function loadPagedEvents(
  stores: SearchStores,
  terms: string[],
  rows: LocalSearchEventRow[],
  pageSize: number,
  cursorRow?: StoredLocalSearchEventRow,
  roomId?: string,
): void {
  const range = cursorRow ? IDBKeyRange.upperBound(cursorRow.ts) : null;
  const req = stores.events.index("ts").openCursor(range, "prev");
  let groupTs: number | null = null;
  let group: StoredLocalSearchEventRow[] = [];
  req.onsuccess = () => {
    const cursor = req.result;
    if (!cursor) {
      processTimestampGroup(stores, terms, group, rows, pageSize, cursorRow, roomId);
      return;
    }
    const row = hydrateEventRow(cursor.value as StoredLocalSearchEventRow);
    if (groupTs !== null && row.ts !== groupTs) {
      processTimestampGroup(stores, terms, group, rows, pageSize, cursorRow, roomId, () => {
        if (rows.length > pageSize) return;
        group = [row];
        groupTs = row.ts;
        cursor.continue();
      });
      return;
    }
    group.push(row);
    groupTs = row.ts;
    cursor.continue();
  };
}

function processTimestampGroup(
  stores: SearchStores,
  terms: string[],
  group: StoredLocalSearchEventRow[],
  rows: LocalSearchEventRow[],
  pageSize: number,
  cursorRow?: StoredLocalSearchEventRow,
  roomId?: string,
  onDone?: () => void,
): void {
  const sorted = group.sort((a, b) => a.eventId.localeCompare(b.eventId));
  processCandidateRow(stores, terms, sorted, 0, rows, pageSize, cursorRow, roomId, onDone);
}

function processCandidateRow(
  stores: SearchStores,
  terms: string[],
  candidates: StoredLocalSearchEventRow[],
  index: number,
  rows: LocalSearchEventRow[],
  pageSize: number,
  cursorRow?: StoredLocalSearchEventRow,
  roomId?: string,
  onDone?: () => void,
): void {
  let currentIndex = index;
  while (true) {
    const row = candidates[currentIndex];
    if (!row || rows.length > pageSize) {
      onDone?.();
      return;
    }
    if (cursorRow && compareSearchRows(row, cursorRow) <= 0) {
      currentIndex += 1;
      continue;
    }
    if (roomId && row.roomId !== roomId) {
      currentIndex += 1;
      continue;
    }
    eventHasTerms(stores.postings, row.eventId, terms, (matches) => {
      if (matches) rows.push(publicEventRow(row));
      processCandidateRow(
        stores,
        terms,
        candidates,
        currentIndex + 1,
        rows,
        pageSize,
        cursorRow,
        roomId,
        onDone,
      );
    });
    return;
  }
}

function eventHasTerms(
  postings: IDBObjectStore,
  eventId: string,
  terms: string[],
  onDone: (matches: boolean) => void,
  index = 0,
): void {
  const term = terms[index];
  if (!term) {
    onDone(true);
    return;
  }
  const req = postings.get(postingId(term, eventId));
  req.onsuccess = () => {
    const row = req.result as PostingRow | undefined;
    if (!row || row.term !== term || row.eventId !== eventId) {
      onDone(false);
      return;
    }
    eventHasTerms(postings, eventId, terms, onDone, index + 1);
  };
}

function compareSearchRows(
  left: Pick<StoredLocalSearchEventRow, "eventId" | "ts">,
  right: Pick<StoredLocalSearchEventRow, "eventId" | "ts">,
): number {
  return right.ts - left.ts || left.eventId.localeCompare(right.eventId);
}

function normaliseEventRow(event: LocalSearchEventInput): NormalisedLocalSearchEventRow | null {
  const body = Array.from(event.body).slice(0, MAX_BODY_CHARS).join("");
  if (!event.eventId || !event.roomId || body.trim().length === 0) return null;
  const terms = tokeniseSearchText(body);
  return { ...event, body, terms, sizeBytes: estimateEventSizeBytes({ ...event, body }, terms) };
}

function openSearchDb(userId: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(localSearchDbName(userId), DB_VERSION);
    let shouldEvictAfterUpgrade = false;
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const oldVersion = event.oldVersion;
      if (!db.objectStoreNames.contains(EVENTS_STORE)) {
        const events = db.createObjectStore(EVENTS_STORE, { keyPath: "eventId" });
        events.createIndex("roomId", "roomId", { unique: false });
        events.createIndex("ts", "ts", { unique: false });
      } else {
        const events = req.transaction?.objectStore(EVENTS_STORE);
        if (events && !events.indexNames.contains("ts")) {
          events.createIndex("ts", "ts", { unique: false });
        }
        if (events && oldVersion < 2) {
          backfillMissingEventMetadata(events);
          shouldEvictAfterUpgrade = true;
        }
      }
      if (!db.objectStoreNames.contains(POSTINGS_STORE)) {
        const postings = db.createObjectStore(POSTINGS_STORE, { keyPath: "id" });
        postings.createIndex("term", "term", { unique: false });
        postings.createIndex("eventId", "eventId", { unique: false });
      }
    };
    req.onsuccess = () => {
      if (shouldEvictAfterUpgrade) {
        queueMicrotask(() => void evictLocalSearchIndex(userId).catch(() => undefined));
      }
      resolve(req.result);
    };
    req.onerror = () => reject(req.error);
  });
}

function backfillMissingEventMetadata(events: IDBObjectStore): void {
  const req = events.openCursor();
  req.onsuccess = () => {
    const cursor = req.result;
    if (!cursor) return;
    const row = cursor.value as Partial<StoredLocalSearchEventRow>;
    if (typeof row.ts !== "number" || typeof row.sizeBytes !== "number") {
      const body = typeof row.body === "string" ? row.body : "";
      cursor.update({
        ...row,
        ts: typeof row.ts === "number" ? row.ts : 0,
        sizeBytes: estimateEventSizeBytes({ ...row, body }, tokeniseSearchText(body)),
      });
    }
    cursor.continue();
  };
}

async function evictLocalSearchIndexNow(userId: string, targetBytes: number): Promise<void> {
  await withSearchStores(userId, "readwrite", (stores) => {
    calculateIndexSize(stores.events, (totalBytes) => {
      if (totalBytes <= targetBytes) return;
      collectOldestEventIdsForEviction(stores.events, totalBytes, targetBytes, (eventIds) => {
        deleteEventsSequentially(stores, eventIds, 0, () => undefined);
      });
    });
  });
}

function calculateIndexSize(events: IDBObjectStore, onDone: (totalBytes: number) => void): void {
  let totalBytes = 0;
  const req = events.openCursor();
  req.onsuccess = () => {
    const cursor = req.result;
    if (!cursor) {
      onDone(totalBytes);
      return;
    }
    const row = hydrateEventRow(cursor.value as StoredLocalSearchEventRow);
    totalBytes += row.sizeBytes;
    cursor.continue();
  };
}

function collectOldestEventIdsForEviction(
  events: IDBObjectStore,
  totalBytes: number,
  targetBytes: number,
  onDone: (eventIds: string[]) => void,
): void {
  const eventIds: string[] = [];
  const req = events.index("ts").openCursor(null, "next");
  req.onsuccess = () => {
    const cursor = req.result;
    if (!cursor || totalBytes <= targetBytes) {
      onDone(eventIds);
      return;
    }
    const row = hydrateEventRow(cursor.value as StoredLocalSearchEventRow);
    eventIds.push(row.eventId);
    totalBytes -= row.sizeBytes;
    cursor.continue();
  };
}

function scheduleEvictionProbe(
  userId: string,
  inserted: number,
  config: LocalSearchIndexConfig,
): void {
  if (inserted === 0) return;
  const interval = config.evictionProbeInterval ?? INDEX_EVICTION_PROBE_INTERVAL;
  if (interval <= 0) return;
  const count = (insertCounts.get(userId) ?? 0) + inserted;
  if (count < interval) {
    insertCounts.set(userId, count);
    return;
  }
  insertCounts.set(userId, count % interval);
  void evictLocalSearchIndex(userId, config.maxIndexBytes ?? MAX_INDEX_BYTES).catch(
    () => undefined,
  );
}

function hydrateEventRow(row: StoredLocalSearchEventRow): StoredLocalSearchEventRow {
  const ts = typeof row.ts === "number" && Number.isFinite(row.ts) ? row.ts : 0;
  if (typeof row.sizeBytes === "number" && Number.isFinite(row.sizeBytes)) return { ...row, ts };
  const terms = tokeniseSearchText(row.body);
  return { ...row, ts, sizeBytes: estimateEventSizeBytes({ ...row, ts }, terms) };
}

function estimateEventSizeBytes(event: Partial<LocalSearchEventRow>, terms: string[]): number {
  const eventIdBytes = textBytes(event.eventId ?? "");
  const indexedTermsBytes = terms.reduce(
    (total, term) => total + textBytes(term) * 2 + eventIdBytes * 2 + POSTING_ROW_OVERHEAD_BYTES,
    0,
  );
  return (
    textBytes(event.body ?? "") +
    eventIdBytes +
    textBytes(event.roomId ?? "") +
    textBytes(event.sender ?? "") +
    textBytes(event.msgtype ?? "") +
    8 +
    EVENT_ROW_OVERHEAD_BYTES +
    indexedTermsBytes
  );
}

function textBytes(value: string): number {
  return textEncoder.encode(value).byteLength;
}

interface SearchStores {
  events: IDBObjectStore;
  postings: IDBObjectStore;
}

async function withSearchStores<T>(
  userId: string,
  mode: IDBTransactionMode,
  run: (stores: SearchStores) => IDBRequest<T> | void,
): Promise<T | undefined> {
  const db = await openSearchDb(userId);
  return await new Promise<T | undefined>((resolve, reject) => {
    const tx = db.transaction([EVENTS_STORE, POSTINGS_STORE], mode);
    const stores = {
      events: tx.objectStore(EVENTS_STORE),
      postings: tx.objectStore(POSTINGS_STORE),
    };
    let value: T | undefined;
    const request = run(stores);
    if (request) {
      request.onsuccess = () => {
        value = request.result;
      };
    }
    tx.oncomplete = () => {
      db.close();
      resolve(value);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
    tx.onabort = () => {
      db.close();
      reject(tx.error ?? new Error("local search tx aborted"));
    };
  });
}

function writeRowsSequentially(
  stores: SearchStores,
  rows: NormalisedLocalSearchEventRow[],
  index: number,
): void {
  const row = rows[index];
  if (!row) return;
  deletePostingsForEvent(stores.postings, row.eventId, () => {
    stores.events.put(storedEventRow(row));
    for (const term of row.terms) {
      stores.postings.put({ id: postingId(term, row.eventId), term, eventId: row.eventId });
    }
    writeRowsSequentially(stores, rows, index + 1);
  });
}

function storedEventRow(row: NormalisedLocalSearchEventRow): StoredLocalSearchEventRow {
  return {
    eventId: row.eventId,
    roomId: row.roomId,
    sender: row.sender,
    ts: row.ts,
    body: row.body,
    sizeBytes: row.sizeBytes,
    ...(row.msgtype ? { msgtype: row.msgtype } : {}),
  };
}

function deleteEventsSequentially(
  stores: SearchStores,
  eventIds: string[],
  index: number,
  onDone: () => void,
): void {
  const eventId = eventIds[index];
  if (!eventId) {
    onDone();
    return;
  }
  stores.events.delete(eventId);
  deletePostingsForEvent(stores.postings, eventId, () => {
    deleteEventsSequentially(stores, eventIds, index + 1, onDone);
  });
}

function deletePostingsForEvent(
  postings: IDBObjectStore,
  eventId: string,
  onDone?: () => void,
): void {
  const req = postings.index("eventId").openCursor(IDBKeyRange.only(eventId));
  req.onsuccess = () => {
    const cursor = req.result;
    if (!cursor) {
      onDone?.();
      return;
    }
    cursor.delete();
    cursor.continue();
  };
}

function publicEventRow(row: StoredLocalSearchEventRow): LocalSearchEventRow {
  return {
    eventId: row.eventId,
    roomId: row.roomId,
    sender: row.sender,
    ts: row.ts,
    body: row.body,
    ...(row.msgtype ? { msgtype: row.msgtype } : {}),
  };
}

function postingId(term: string, eventId: string): string {
  return `${term}\u0000${eventId}`;
}
